import React, { useState, useEffect } from 'react';
import { useWindowBasketStore, WindowBasketItem } from '../hooks/useWindowBasket';
import { useWindowStylesWatcher } from '../hooks/useWindowStylesWatcher';
import { 
  Box, 
  Typography,
  Grid,
  Card,
  CardContent,
  Container,
  Button,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Drawer,
  Badge,
  IconButton,
  Tabs,
  TabList,
  Tab,
  Input,
  Textarea,
  FormHelperText,
  Checkbox,
  Alert,
  Select,
  Option
} from '@mui/joy';
import ImageCanvasViewer from './ImageCanvasViewer';
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaShoppingCart, 
  FaMinus, 
  FaPlus, 
  FaTimes 
} from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import WindowTypeSelector from './WindowTypeSelector';
import WindowBasket from './WindowBasket';
import { useNavigate } from 'react-router-dom';
 

// Define a function to extract the name from the filename
export const getNameFromFilePath = (path: string): string => {
  // Get the filename from the path
  const filename = path.split('/').pop() || '';
  
  // Remove the extension and replace hyphens with spaces
  const name = filename.replace(/\.[^/.]+$/, '').replace(/-/g, ' ');
  
  // Capitalize each word
  return name.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Define a function to generate a description from the name
export const getDescriptionFromName = (name: string): string => {
  // Return just the name without adding "window design"
  return name;
};

// Function to determine the category based on the file path
export const getCategoryFromPath = (path: string): string => {
  if (path.includes('/Traditional/')) {
    return 'traditional';
  } else if (path.includes('/Top-Hung/')) {
    return 'top-hung';
  } else if (path.includes('/Fixed/')) {
    return 'fixed';
  } else if (path.includes('/Bespoke/')) {
    return 'bespoke';
  } else {
    // Determine category based on filename for backwards compatibility
    const filename = path.split('/').pop() || '';
    if (filename.toLowerCase().includes('traditional')) {
      return 'traditional';
    } else if (filename.toLowerCase().includes('fixed')) {
      return 'fixed';
    } else if (filename.toLowerCase().includes('opener') || 
               filename.toLowerCase().includes('fan') ||
               filename.toLowerCase().includes('hung')) {
      return 'top-hung';
    } else {
      return 'other';
    }
  }
};

// Function to create a WindowStyle object from a file path
export const createWindowStyle = (id: number, imagePath: string, category: 'traditional' | 'topHung' | 'fixed' | 'other' | 'bespoke' = 'traditional'): WindowStyle => {
  const name = getNameFromFilePath(imagePath);
  
  return {
    id,
    name,
    imagePath,
    previewPath: imagePath,
    description: getDescriptionFromName(name),
    category
  };
};

// Define the image paths available in the public/images/windows directory
export const traditionalPaths = [
  '/images/windows/Casements/Traditional/Traditional Style Four - Bottom Openers.jpg',
  '/images/windows/Casements/Traditional/Traditional Style Four.jpg',
  '/images/windows/Casements/Traditional/Traditional Style Three.jpg',
  '/images/windows/Casements/Traditional/Traditional Style Two.jpg',
  '/images/windows/Casements/Traditional/Traditional Style One.jpg',
  '/images/windows/Casements/Traditional/Traditional Style Six.jpg',
  '/images/windows/Casements/Traditional/Traditional Style Five.jpg',
  '/images/windows/Casements/Traditional/Traditional Right Large Fixed Pane.jpg',
  '/images/windows/Casements/Traditional/Traditional Left Large Fixed Pane.jpg',
  '/images/windows/Casements/Traditional/Right Hung Traditional.jpg',
  '/images/windows/Casements/Traditional/Left Hung Traditional.jpg',
  '/images/windows/Casements/Traditional/Double Casement.jpg',
  '/images/windows/Casements/Traditional/Middle Opener - Triple Section Two.jpg',
  '/images/windows/Casements/Traditional/Middle Opener - Triple Section One.jpg',
  '/images/windows/Casements/Traditional/Right Side Hung Opener.jpg',
  '/images/windows/Casements/Traditional/Left Side Hung Opener.jpg',
  '/images/windows/Casements/Traditional/Right-Side Hung.jpg',
  '/images/windows/Casements/Traditional/Left Side-Hung.jpg',
];

export const topHungPaths = [
  '/images/windows/Casements/Top-Hung/Traditional Style Three - Bottom Opener.jpg',
  '/images/windows/Casements/Top-Hung/Traditional Style Two - Bottom Openers.jpg',
  '/images/windows/Casements/Top-Hung/Traditional Style Bottom Openers.jpg',
  '/images/windows/Casements/Top-Hung/Triple Fan Openers.jpg',
  '/images/windows/Casements/Top-Hung/Middle Bottom Openers - Triple Section.jpg',
  '/images/windows/Casements/Top-Hung/Right Bottom Openers - Fixed Large Left.jpg',
  '/images/windows/Casements/Top-Hung/Left Bottom Openers - Fixed Large Right.jpg',
  '/images/windows/Casements/Top-Hung/Right Bottom Openers - Left Fixed.jpg',
  '/images/windows/Casements/Top-Hung/Left Bottom Openers - Right Fixed.jpg',
  '/images/windows/Casements/Top-Hung/Right Bottom Opener Traditional.jpg',
  '/images/windows/Casements/Top-Hung/Left Botom Opener Traditional.jpg',
  '/images/windows/Casements/Top-Hung/Double Fan Opener - Fixed Split.jpg',
  '/images/windows/Casements/Top-Hung/Double Fan Opener - Fixed Full Width.jpg',
  '/images/windows/Casements/Top-Hung/Double Bottom Opener.jpg',
  '/images/windows/Casements/Top-Hung/Fan & Bottom Opener.jpg',
  '/images/windows/Casements/Top-Hung/Middle Bottom Opener.jpg',
  '/images/windows/Casements/Top-Hung/Single Fan Top Right - Triple Section.jpg',
  '/images/windows/Casements/Top-Hung/Single Fan Top Middle - Triple Section.jpg',
  '/images/windows/Casements/Top-Hung/Single Fan Top Left - Triple Section.jpg',
  '/images/windows/Casements/Top-Hung/Single Fan Top Right.jpg',
  '/images/windows/Casements/Top-Hung/Single Fan Top Left.jpg',
  '/images/windows/Casements/Top-Hung/Right Bottom Opener - Fixed Left.jpg',
  '/images/windows/Casements/Top-Hung/Left Bottom Opener - Fixed Right.jpg',
  '/images/windows/Casements/Top-Hung/Button Opener Fixed Fan.jpg',
  '/images/windows/Casements/Top-Hung/Fan Light Opener Only.jpg',
  '/images/windows/Casements/Top-Hung/Single Full Opener.jpg',
  '/images/windows/Casements/Top-Hung/Square Openers One.jpg',
];

export const fixedPaths = [
  '/images/windows/Casements/Fixed/Fixed Double & Fixed Fans.jpg',
  '/images/windows/Casements/Fixed/Fixed Double Portrait.jpg',
  '/images/windows/Casements/Fixed/Fixed Double Landscape.jpg',
  '/images/windows/Casements/Fixed/Fixed Fan Light.jpg',
  '/images/windows/Casements/Fixed/Single Fixed.jpg',
  '/images/windows/Casements/Fixed/Fixed Triple Portrait.jpg',
  '/images/windows/Casements/Fixed/Fixed Triple Landscape.jpg',
];

// Add tilt-and-turn paths
export const tiltAndTurnPaths = [
  '/images/windows/TiltAndTurn/Button Opener Fixed Fan.jpg',
  '/images/windows/TiltAndTurn/Double Casement.jpg',
  '/images/windows/TiltAndTurn/Left Side-Hung.jpg',
  '/images/windows/TiltAndTurn/Middle Bottom Opener.jpg',
  '/images/windows/TiltAndTurn/Middle Opener - Triple Section One.jpg',
  '/images/windows/TiltAndTurn/Quad Casement.jpg',
  '/images/windows/TiltAndTurn/Right-Side Hung.jpg',
  '/images/windows/TiltAndTurn/Single Full Opener.jpg',
  '/images/windows/TiltAndTurn/Traditional Style Bottom Openers.jpg',
];

export const bespokePaths = [
  '/images/windows/Casements/Bespoke/Bespoke One.jpg',
  '/images/windows/Casements/Bespoke/Bespoke Two.jpg',
];

export const allPaths = [...traditionalPaths, ...topHungPaths, ...fixedPaths, ...tiltAndTurnPaths, ...bespokePaths ];

// Create the window styles dynamically from the file paths
const traditionalStyles = traditionalPaths.map((path, index) => 
  createWindowStyle(index + 1, path, 'traditional')
);

const topHungStyles = topHungPaths.map((path, index) => 
  createWindowStyle(traditionalStyles.length + index + 1, path, 'topHung')
);

const fixedStyles = fixedPaths.map((path, index) => 
  createWindowStyle(traditionalStyles.length + topHungStyles.length + index + 1, path, 'fixed')
);

// Create tilt-and-turn styles
const tiltAndTurnStyles = tiltAndTurnPaths.map((path, index) => 
  createWindowStyle(traditionalStyles.length + topHungStyles.length + fixedStyles.length + index + 1, path, 'other')
);

const bespokeStyles = bespokePaths.map((path, index) =>
  createWindowStyle(traditionalStyles.length + topHungStyles.length + fixedStyles.length + tiltAndTurnStyles.length + index + 1,
    path,
    'bespoke'
  )
);

const allStyles = [...traditionalStyles, ...topHungStyles, ...fixedStyles, ...tiltAndTurnStyles, ...bespokeStyles];

// Create the window styles by type mapping
const windowStylesByType: Record<string, WindowStyle[]> = {
  'casement': allStyles.filter(style => 
    style.category === 'traditional' || 
    style.category === 'topHung' || 
    style.category === 'fixed' ||
    style.category === 'bespoke'
  ),
  'flush-casement': allStyles.filter(style => 
    style.category === 'traditional' || 
    style.category === 'topHung' || 
    style.category === 'fixed' ||
    style.category === 'bespoke'
  ),
  'aluminium': allStyles.filter(style => 
    style.category === 'traditional' || 
    style.category === 'topHung' || 
    style.category === 'fixed' ||
    style.category === 'bespoke'
  ),
  'tilt-and-turn': tiltAndTurnStyles,
  'sliding-sash': []
};

// Helper to load all images from a folder (pseudo, replace with actual loader if needed)
function loadStylesFrom(folder: string): WindowStyle[] {
  // For now, just load the single image in the folder as a style
  // In a real app, you would dynamically read all images in the folder
  if (folder === 'Casements') {
    return allStyles; // existing logic
  }
  if (folder === 'Tilt & Turn') {
    return [createWindowStyle(1, 'images/windows/Tilt & Turn/Single Fixed.jpg')];
  }
  if (folder === 'Sliding Sash') {
    return [createWindowStyle(1, 'images/windows/Sliding Sash/Single Fixed.jpg')];
  }
  return [];
}

// Export the interface and styles for use in other components
export interface WindowStyle {
  id: number;
  name: string;
  imagePath: string;
  previewPath: string;
  description?: string;
  category: 'traditional' | 'topHung' | 'fixed' | 'other' | 'bespoke';
}

// Define window types
const windowTypes = [
  { id: 'casement', name: 'Casement' },
  { id: 'flush-casement', name: 'Flush Casement' },
  { id: 'aluminium', name: 'Aluminium' },
  { id: 'tilt-and-turn', name: 'Tilt & Turn' },
  { id: 'sliding-sash', name: 'Sliding Sash' }
];

const WindowDesigner: React.FC = () => {
  
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<number | null>(null);
  const [frameColor, setFrameColor] = useState<string>('white-smooth-both');
  const [glassType, setGlassType] = useState<string>('clear');
  const [clearGlassType, setClearGlassType] = useState<'float' | 'toughened' | 'laminated'>('float');
  const [obscuredGlassType, setObscuredGlassType] = useState<string>('Arctic');
  const [basketOpen, setBasketOpen] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<WindowBasketItem | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [styleFilter, setStyleFilter] = useState<'all' | 'traditional' | 'top-hung' | 'fixed'>('all');
  const itemsPerPage = 12;
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [profileDepth, setProfileDepth] = useState<number | undefined>(70);
  const [profileType, setProfileType] = useState<'Sculptured Profile' | 'Chamfered Profile' | ''>('Chamfered Profile');
  const [beadingType, setBeadingType] = useState<'Sculptured (Ovolo)' | 'Chamfered'>('Chamfered');
  const [cillRequired, setCillRequired] = useState<boolean>(false);
  const [cillType, setCillType] = useState<'Regular' | 'Stub' | ''>('');
  const [notes, setNotes] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [editingReference, setEditingReference] = useState<boolean>(false);
  const [localReference, setLocalReference] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [originalStyle, setOriginalStyle] = useState<number | null>(null);
  const [cillIncluded, setCillIncluded] = useState<boolean>(false);
  const [cillHeight, setCillHeight] = useState<number>(30);
  const [trickleVents, setTrickleVents] = useState<boolean>(false);
  const [testNotification, setTestNotification] = useState<string | null>(null);
  
  const { 
    addItem, 
    items, 
    getItemCount, 
    customerReference, 
    setCustomerReference 
  } = useWindowBasketStore();

  const { windowStyles, lastUpdate } = useWindowStylesWatcher();
  const navigate = useNavigate();
  
  // Check URL query parameters for step=types
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const step = urlParams.get('step');
    
    if (step === 'types') {
      // Force view to window type selection
      setSelectedType(null);
      setSelectedStyle(null);
      
      // Clean URL by removing the query parameter
      navigate('/window-designer', { replace: true });
    }
  }, [navigate]);

  // Debug: Log the styles available
  useEffect(() => {
    console.log("Fixed styles count:", fixedStyles.length);
    console.log("Fixed styles:", fixedStyles);
    console.log("Traditional styles count:", traditionalStyles.length);
    console.log("All styles count:", allStyles.length);
    console.log("WindowStyles from watcher count:", windowStyles.length);

    // Check for Single Fixed window style in allStyles
    const singleFixed = allStyles.find(style => style.name.includes("Single Fixed"));
    console.log("Single Fixed in allStyles:", singleFixed);
    
    // Check for Traditional Style One in allStyles
    const traditionalOne = allStyles.find(style => style.name.includes("Traditional Style One"));
    console.log("Traditional Style One in allStyles:", traditionalOne);

    // Check ID mappings
    if (singleFixed) console.log("Single Fixed ID:", singleFixed.id);
    if (traditionalOne) console.log("Traditional One ID:", traditionalOne.id);
  }, [windowStyles.length]);

  // Function to reset all editor state to defaults
  const resetEditorState = (notificationMessage?: string) => {
    // Reset the editing state
    setEditingItem(null);
    setOriginalStyle(null);
    setSelectedType(null);
    setSelectedStyle(null);
    
    // Reset form values
    setFrameColor('white-smooth-both');
    setGlassType('clear');
    setClearGlassType('float');
    setObscuredGlassType('Arctic');
    setWidth(undefined);
    setHeight(undefined);
    setProfileDepth(70);
    setProfileType('Chamfered Profile');
    setBeadingType('Chamfered');
    setCillRequired(false);
    setCillIncluded(false);
    setCillHeight(30);
    setCillType('');
    setTrickleVents(false);
    setNotes('');
    setReference('');
    
    // Show notification if provided
    if (notificationMessage) {
      setTestNotification(notificationMessage);
      setTimeout(() => setTestNotification(null), 5000);
    }
  };

  // Set local reference from store's customer reference when component mounts
  useEffect(() => {
    setLocalReference(customerReference);
  }, [customerReference]);

  useEffect(() => {
    // Reset the selected style and pagination when changing window type
    if (selectedType) {
      setSelectedStyle(null);
      setCurrentPage(1);
      // Reset specifications
      setWidth(undefined);
      setHeight(undefined);
      setProfileDepth(70);
      // Set default profile type for all window types
      setProfileType('Chamfered Profile');
      setBeadingType('Chamfered'); // Reset beading type
      setGlassType('clear');
      setClearGlassType('float');
      setObscuredGlassType('Arctic');
      setCillRequired(false);
      setCillIncluded(false);
      setCillType('');
      setTrickleVents(false);
      setNotes('');
      setReference('');
    }
  }, [selectedType]);

  useEffect(() => {
    // Reset pagination when changing filter
    setCurrentPage(1);
    // Reset selected style when changing filter
    setSelectedStyle(null);
    
    // Debug log for filter change
    console.log(`Filter changed to: ${styleFilter}`);
    
    // Log window styles with their categories
    const categoryCounts = windowStyles.reduce((acc, style) => {
      acc[style.category] = (acc[style.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    console.log('Window styles by category:', categoryCounts);
  }, [styleFilter]);

  useEffect(() => {
    // When editing, ensure we're strictly using the values from editingItem
    if (editingItem) {
      console.log("Editing item:", editingItem); // Debug log
      
      // Clear the current page to ensure we can properly navigate
      setCurrentPage(1);
      
      // First set the window type - this must happen first since it affects available styles
      setSelectedType(editingItem.windowType);
      
      // Force a slight delay to ensure window type is fully processed before setting style
      // This prevents the style from being lost when type changes trigger style resets
      // Store editingItem in local variable to prevent closure issues
      const itemToEdit = {...editingItem};
      
      setTimeout(() => {
        // Make sure editingItem is still valid (user didn't cancel during timeout)
        if (!itemToEdit) return;
        
        // We intentionally don't set the selectedStyle here
        // Instead, we let the user choose to keep the same style or pick a new one
        // The original style is already set in the handleEditItem function
        
        // Set frame and glass options
        setFrameColor(itemToEdit.frameColor || 'white-smooth-both');
        setGlassType(itemToEdit.glassType || 'clear');
        
        if (itemToEdit.glassType === 'clear' && itemToEdit.clearGlassType) {
          setClearGlassType(itemToEdit.clearGlassType);
        } else {
          setClearGlassType('float'); // Default
        }
        
        // Set obscured glass type if applicable
        if (itemToEdit.glassType === 'frosted' && itemToEdit.obscuredGlassType) {
          setObscuredGlassType(itemToEdit.obscuredGlassType);
        } else {
          setObscuredGlassType('Arctic'); // Default
        }
        
        // Set dimensions
        setWidth(itemToEdit.width);
        setHeight(itemToEdit.height);
        
        // Set profile options - ensure we don't lose these values
        setProfileDepth(itemToEdit.profileDepth !== undefined ? itemToEdit.profileDepth : 70);
        
        // Explicitly set profileType - critical issue to fix
        console.log("Profile Type:", itemToEdit.profileType); // Debug log
        if (itemToEdit.profileType === 'Sculptured Profile') {
          setProfileType('Sculptured Profile');
        } else if (itemToEdit.profileType === 'Chamfered Profile') {
          setProfileType('Chamfered Profile');
        } else {
          setProfileType('Chamfered Profile');
        }
        
        // Set beading type
        setBeadingType(itemToEdit.beadingType || 'Chamfered');
        
        // Set cill options
        setCillIncluded(itemToEdit.cillIncluded === true);
        setCillRequired(itemToEdit.cillRequired === true);
        setCillType(itemToEdit.cillType || '');
        setCillHeight(itemToEdit.cillHeight || 30);
        
        // Set trickle vents
        setTrickleVents(itemToEdit.trickleVents === true);
        
        // Set notes and reference - ensure reference is explicitly set
        setNotes(itemToEdit.notes || '');
        console.log("Window Reference:", itemToEdit.reference); // Debug log
        setReference(itemToEdit.reference || '');
        
        // Set quantity
        setQuantity(itemToEdit.quantity || 1);
      }, 50); // Short delay to ensure type is processed first
    } else {
      // Only reset originalStyle if we're not in an edit operation
      // This is important - don't reset originalStyle when editingItem changes to null during normal flow
      // Only the handleCancelEdit and resetEditorState functions should reset originalStyle
    }
  }, [editingItem]);

  useEffect(() => {
    // If we're editing and the current style was removed, go back to style selection
    if (editingItem && selectedStyle) {
      const styleExists = windowStyles.some(style => style.id === selectedStyle);
      if (!styleExists) {
        setSelectedStyle(null);
        setEditingItem(null);
        // Show notification
        setTestNotification('The window style you were editing has been removed');
        setTimeout(() => setTestNotification(null), 5000);
      }
    }
  }, [lastUpdate]); // Run when styles are updated
  
  // Listen for window basket item removal events
  useEffect(() => {
    // Event handler for when an item is removed from the basket
    const handleItemRemoved = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { itemId } = customEvent.detail;
      
      // Check if the removed item is the one we're currently editing
      if (editingItem && editingItem.id === itemId) {
        console.log("Item being edited was removed from basket, resetting editor state");
        resetEditorState('The item you were editing has been removed from the basket');
      }
    };
    
    // Add event listener
    window.addEventListener('window-basket-item-removed', handleItemRemoved);
    
    // Cleanup function
    return () => {
      window.removeEventListener('window-basket-item-removed', handleItemRemoved);
    };
  }, [editingItem]); // Only re-subscribe when editingItem changes

  const handleTypeSelect = (typeId: string) => {
    setSelectedType(typeId);
  };

  const handleStyleSelect = (styleId: number) => {
    setSelectedStyle(styleId);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(getTotalPages(), prev + 1));
  };

  const handleAddToQuote = () => {
    
    // Basic validation (static)
    if (!selectedType || !selectedStyle) return;
    
    // Ensure reference is provided
    if (!reference.trim()) {
      alert('Please provide a window reference');
      return;
    }
    
    // Ensure profile type is provided for all window types
    if (!profileType) {
      alert('Please select a profile type');
      return;
    }
    
    // Find the selected style to get its image path (use correct type)
    const stylesForType = windowStylesByType[selectedType] || [];
    const selectedStyleObj = stylesForType.find((style) => style.id === selectedStyle);
    const imagePath = selectedStyleObj?.previewPath || '';
    
    // Add new item to basket
    console.log('[Designer] Adding item to basket:', {
      windowType: selectedType,
      windowStyle: selectedStyle,
      imagePath: imagePath,
      // ...other fields omitted for brevity
    });
    addItem({
      windowType: selectedType,
      windowStyle: selectedStyle,
      frameColor: frameColor,
      glassType: glassType,
      clearGlassType: glassType === 'clear' ? clearGlassType : undefined,
      obscuredGlassType: glassType === 'frosted' ? obscuredGlassType : undefined,
      quantity: quantity,
      price: 0, // No price needed
      imagePath: imagePath, // Store the exact image path
      // Include specifications
      width,
      height,
      profileDepth,
      profileType: profileType || undefined,
      beadingType,
      cillIncluded,
      cillRequired: !cillIncluded && cillRequired,
      cillHeight: !cillIncluded && cillRequired ? cillHeight : undefined,
      cillType: (cillIncluded || (!cillIncluded && cillRequired)) ? cillType : undefined,
      trickleVents,
      notes,
      reference
    });
    
    // Show basket
    setBasketOpen(true);
  };

  const handleEditItem = (item: WindowBasketItem) => {
    console.log("Starting edit with item:", item);
    console.log("Profile Type:", item.profileType);
    console.log("Reference:", item.reference);
    console.log("Window Style ID:", item.windowStyle);
    console.log("Image Path:", item.imagePath);
    
    // Reset the current page to ensure we start fresh
    setCurrentPage(1);
    
    // Ensure the item has all required properties before starting edit
    const completeItem: WindowBasketItem = {
      ...item,
      // Ensure these critical fields are explicitly defined with default values if missing
      windowType: item.windowType || 'casement',
      windowStyle: item.windowStyle || 1,
      frameColor: item.frameColor || 'white-smooth-both',
      glassType: item.glassType || 'clear',
      clearGlassType: item.glassType === 'clear' ? (item.clearGlassType || 'float') : undefined,
      obscuredGlassType: item.glassType === 'frosted' ? (item.obscuredGlassType || 'Arctic') : undefined,
      profileType: item.profileType || 'Chamfered Profile',
      reference: item.reference || '',
      quantity: item.quantity || 1
    };
    
    // First set the window type directly - this needs to happen before setting editingItem
    // to prevent racing conditions with the useEffect
    setSelectedType(completeItem.windowType);
    
    // CRITICAL: Set the original style based on the image path
    // This is the most reliable way to identify the actual window style
    let foundStyle: WindowStyle | undefined;
    
    if (completeItem.imagePath) {
      // First try to find style by exact image path
      foundStyle = allStyles.find(s => s.previewPath === completeItem.imagePath);
      
      if (!foundStyle) {
        // If exact path doesn't match, try to find by image filename
        const imageFilename = completeItem.imagePath.split('/').pop() || '';
        foundStyle = allStyles.find(s => s.previewPath.includes(imageFilename));
      }
      
      if (foundStyle) {
        console.log("Found style using image path:", foundStyle.name, "ID:", foundStyle.id);
        // Set original style to the found style ID
        setOriginalStyle(foundStyle.id);
      } else {
        console.log("Style not found by image path, falling back to ID");
        setOriginalStyle(completeItem.windowStyle);
      }
    } else {
      console.log("No image path available, using windowStyle ID:", completeItem.windowStyle);
      setOriginalStyle(completeItem.windowStyle);
    }
    
    // Set selectedStyle to null to ensure the style selection screen is shown initially
    setSelectedStyle(null);
    
    // Make sure we're working with the complete item
    // This is a direct assignment that will trigger the useEffect
    setEditingItem(completeItem);
    
    // Close the basket to show the editor
    setBasketOpen(false);
  };

  const handleSaveEdit = () => {
    if (!editingItem || !selectedType || !selectedStyle) return;
    
    // Ensure reference is provided
    if (!reference.trim()) {
      alert('Please provide a window reference');
      return;
    }
    
    // Ensure profile type is provided for all window types
    if (!profileType) {
      alert('Please select a profile type');
      return;
    }
    
    // Find the selected style to get its image path (use correct type)
    const stylesForType = windowStylesByType[selectedType] || [];
    const selectedStyleObj = stylesForType.find((style) => style.id === selectedStyle);
    
    if (!selectedStyleObj) {
      console.error("Error: Could not find selected style:", selectedStyle);
      alert('Error: Could not find the selected window style. Please try again.');
      return;
    }
    
    const imagePath = selectedStyleObj.previewPath;
    
    console.log("Saving edit with style ID:", selectedStyle);
    console.log("Style object:", selectedStyleObj);
    console.log("Style name:", selectedStyleObj.name);
    console.log("Image path:", imagePath);
    console.log("Original window style ID:", originalStyle);
    
    // Update the item - preserve all original values not being edited
    console.log('[Designer] Updating item in basket:', {
      id: editingItem.id,
      windowType: selectedType,
      windowStyle: selectedStyle,
      imagePath: imagePath,
      // ...other fields omitted for brevity
    });
    useWindowBasketStore.getState().updateItem(editingItem.id, {
      // Update window type and style
      windowType: selectedType,
      windowStyle: selectedStyle,
      imagePath: imagePath, // Store the exact image path
      
      // Update frame and glass options
      frameColor: frameColor,
      glassType: glassType,
      clearGlassType: glassType === 'clear' ? clearGlassType : undefined,
      obscuredGlassType: glassType === 'frosted' ? obscuredGlassType : undefined,
      
      // Update dimensions
      width: width,
      height: height,
      
      // Update profile options
      profileDepth: profileDepth,
      profileType: profileType || undefined,
      beadingType: beadingType,
      
      // Update cill options
      cillIncluded: cillIncluded,
      cillRequired: !cillIncluded && cillRequired,
      cillHeight: !cillIncluded && cillRequired ? cillHeight : undefined,
      cillType: (cillIncluded || (!cillIncluded && cillRequired)) ? cillType : undefined,
      
      // Update trickle vents option
      trickleVents: trickleVents,
      
      // Update notes and reference
      notes: notes,
      reference: reference,
      
      // Update quantity
      quantity: quantity,
      
      // Preserve original price
      price: editingItem.price
    });
    
    console.log("Edit saved, resetting editor state");
    
    // Clear editing state
    setEditingItem(null);
    
    // Reset form for new entry
    setFrameColor('white-smooth-both');
    setGlassType('clear');
    setClearGlassType('float');
    setObscuredGlassType('Arctic');
    setWidth(undefined);
    setHeight(undefined);
    setProfileDepth(70);
    setProfileType('Chamfered Profile');
    setBeadingType('Chamfered');
    setCillRequired(false);
    setCillIncluded(false);
    setCillHeight(30);
    setCillType('');
    setTrickleVents(false);
    setNotes('');
    setReference('');
    setOriginalStyle(null);
    
    // Show basket
    setBasketOpen(true);
  };

  const handleCancelEdit = () => {
    resetEditorState();
    
    // Show basket again
    setBasketOpen(true);
  };

  const getWindowStyles = () => {
    if (!selectedType) return [];
    
    console.log(`Getting window styles for type: ${selectedType}`);
    
    // Get the styles for the selected window type
    let styles = windowStylesByType[selectedType] || [];
    
    // Apply category filter if needed
    if (styleFilter !== 'all') {
      // Convert filter value to match category value
      const categoryMapping: Record<'traditional' | 'top-hung' | 'fixed' | 'bespoke', string> = {
        'traditional': 'traditional',
        'top-hung': 'topHung',
        'fixed': 'fixed',
        'bespoke': 'bespoke'
      };
      
      const categoryValue = categoryMapping[styleFilter as keyof typeof categoryMapping];
      console.log(`Mapping filter ${styleFilter} to category ${categoryValue}`);
      
      styles = styles.filter(style => style.category === categoryValue);
    }
    
    console.log(`Found ${styles.length} styles after filtering`);
    
    // Paginate the results
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return styles.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    if (!selectedType) return 1;
    
    // Get the styles for the selected window type
    let styles = windowStylesByType[selectedType] || [];
    
    // Apply category filter if needed
    if (styleFilter !== 'all') {
      const categoryMapping: Record<'traditional' | 'top-hung' | 'fixed' | 'bespoke', string> = {
        'traditional': 'traditional',
        'top-hung': 'topHung',
        'fixed': 'fixed',
        'bespoke': 'bespoke'
      };
      
      const categoryValue = categoryMapping[styleFilter as keyof typeof categoryMapping];
      styles = styles.filter(style => style.category === categoryValue);
    }
    
    return Math.ceil(styles.length / itemsPerPage);
  };

  const getTypeName = (typeId: string) => {
    return windowTypes.find(type => type.id === typeId)?.name || '';
  };

  const handleFilterChange = (value: string) => {
    setStyleFilter(value as 'all' | 'traditional' | 'top-hung' | 'fixed');
  };

  // Handle customer reference submission
  const handleReferenceSubmit = (reference: string) => {
    setCustomerReference(reference);
    setEditingReference(false);
  };

  // Compute available categories for the selected window type
  const availableCategories = React.useMemo(() => {
    if (!selectedType) return [];
    const styles = windowStylesByType[selectedType] || [];
    const categories = Array.from(new Set(styles.map((s) => s.category)));
    // Map to filter values used in the UI
    const mapping: Record<string, string> = {
      'traditional': 'traditional',
      'topHung': 'top-hung', 
      'fixed': 'fixed',
      'other': 'other',
      'bespoke': 'bespoke'
    };
    return categories.map(cat => mapping[cat as keyof typeof mapping]).filter(Boolean);
  }, [selectedType]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography level="h1" component="h1" sx={{ textAlign: 'left' }}>
          Window Designer
        </Typography>
        
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Debug button */}
          <Button
            variant="outlined"
            color="neutral"
            size="sm"
            onClick={() => {
              console.log('All window styles:', windowStyles);
              const categoriesSet = new Set(windowStyles.map(style => style.category));
              const categories = Array.from(categoriesSet);
              console.log('Categories:', categories);
              alert(`Found ${windowStyles.length} window styles with categories: ${categories.join(', ')}`);
            }}
            sx={{ display: process.env.NODE_ENV === 'development' ? 'inline-flex' : 'none' }}
          >
            Debug Styles
          </Button>
          
          <Badge badgeContent={getItemCount()} color="primary">
            <IconButton 
              variant="outlined"
              onClick={() => setBasketOpen(true)}
              aria-label="Window quote"
            >
              <IconWrapper icon={FaShoppingCart} />
            </IconButton>
          </Badge>
        </Box>
      </Box>
      
      {/* Display the customer reference at the top if set */}
      {customerReference && !editingReference && (
        <Box 
          sx={{ 
            mb: 3, 
            p: 2, 
            backgroundColor: 'primary.50', 
            borderRadius: 'md',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography level="title-md">
            Customer Reference: <strong>{customerReference}</strong>
          </Typography>
          
          <Button 
            size="sm" 
            variant="plain" 
            color="primary"
            onClick={() => {
              setLocalReference(customerReference);
              setEditingReference(true);
            }}
          >
            Edit
          </Button>
        </Box>
      )}
      
      {/* Edit Reference Form */}
      {editingReference && (
        <Box 
          sx={{ 
            mb: 3, 
            p: { xs: 2, sm: 3 }, 
            backgroundColor: '#f7f7f7', 
            borderRadius: 'md',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Grid container spacing={2} alignItems="flex-end">
            <Grid xs={12} sm={8}>
              <FormControl size="sm" sx={{ mb: { xs: 1, sm: 0 }, width: '100%' }}>
                <FormLabel sx={{ fontSize: '0.95rem', fontWeight: 'medium' }}>Edit Customer Reference</FormLabel>
                <Input
                  value={localReference}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalReference(e.target.value)}
                  placeholder="e.g., Smith Residence, Project #1234"
                  size="lg"
                  sx={{ fontSize: '1rem' }}
                />
              </FormControl>
            </Grid>
            <Grid xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
              <Button 
                type="button" 
                variant="outlined"
                color="neutral"
                onClick={() => setEditingReference(false)}
                size="lg"
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                color="primary"
                onClick={() => handleReferenceSubmit(localReference)}
                disabled={!localReference.trim()}
                size="lg"
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
      
      <Grid container spacing={3}>
        <Grid xs={12}>
          {!customerReference && !editingReference ? (
            // Condensed Customer Reference Form above Window Type Selection
            <Box sx={{ 
              backgroundColor: '#f7f7f7', 
              borderRadius: '4px', 
              p: { xs: 2, sm: 3 }, 
              mb: 3,
              border: '1px solid',
              borderColor: 'divider' 
            }}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid xs={12} sm={8}>
                  <FormControl size="sm" sx={{ mb: { xs: 1, sm: 0 }, width: '100%' }}>
                    <FormLabel sx={{ fontSize: '0.95rem', fontWeight: 'medium' }}>Customer Reference *</FormLabel>
                    <Input
                      value={localReference}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalReference(e.target.value)}
                      placeholder="e.g., Smith Residence, Project #1234"
                      size="lg"
                      sx={{ fontSize: '1rem' }}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <Button 
                    type="button" 
                    color="primary"
                    onClick={() => handleReferenceSubmit(localReference)}
                    disabled={!localReference.trim()}
                    size="lg"
                  >
                    Save Reference
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ) : null}
          
          {!selectedType ? (
            // Step 1: Select window type
            <WindowTypeSelector 
              onSelectType={handleTypeSelect} 
              selectedType={selectedType} 
            />
          ) : (!selectedStyle || (editingItem && !getWindowStyles().find(s => s.id === selectedStyle))) ? (
            // Step 2: Select window style - show this when:
            // 1. No style is selected yet, OR
            // 2. We're editing and need to choose a different style
            <Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3 }}>
                <Button 
                  variant="outlined" 
                  color="neutral" 
                  onClick={() => {
                    if (editingItem) {
                      setSelectedStyle(editingItem.windowStyle);
                    } else {
                      setSelectedType(null);
                    }
                  }}
                  sx={{ mb: { xs: 2, sm: 0 }, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                >
                  {editingItem ? 'Cancel Style Change' : 'Back to Window Types'}
                </Button>
                <Typography level="h2" sx={{ textAlign: { xs: 'left', sm: 'inherit' } }}>
                  {editingItem ? 'Change' : 'Select'} {getTypeName(selectedType)} Style
                </Typography>
              </Box>
              
              {/* Style Category Filter */}
              <Box sx={{ mb: 3 }}>
                <Tabs 
                  value={styleFilter}
                  onChange={(_, value) => handleFilterChange(value as string)}
                  sx={{ 
                    borderRadius: 'lg',
                    boxShadow: 'sm',
                    overflow: 'auto',
                    backgroundColor: 'background.surface',
                  }}
                >
                  <TabList 
                    variant="outlined"
                    sx={{
                      overflowX: 'auto',
                      whiteSpace: 'nowrap',
                      maxWidth: '100vw',
                      scrollbarWidth: 'thin',
                      fontSize: '0.8rem',
                      '&::-webkit-scrollbar': { height: 6 },
                    }}
                  >
                    <Tab value="all">All Styles</Tab>
                    {availableCategories.includes('traditional') && <Tab value="traditional">Traditional</Tab>}
                    {availableCategories.includes('top-hung') && <Tab value="top-hung">Top Hung</Tab>}
                    {availableCategories.includes('fixed') && <Tab value="fixed">Fixed</Tab>}
                    {availableCategories.includes('bespoke') && <Tab value="bespoke">Bespoke</Tab>}
                  </TabList>
                </Tabs>
              </Box>
              
              <Box sx={{ 
                backgroundColor: '#f2f2f2', 
                borderRadius: '4px', 
                px: { xs: 1, sm: 4 }, 
                py: { xs: 2, sm: 4 }, 
                mb: 4 
              }}>
                <Grid container spacing={3}>
                  {getWindowStyles().map((style, idx, arr) => (
                    <Grid 
                      xs={12} sm={6} md={4} 
                      key={style.id}
                      sx={idx === arr.length - 1 ? { mr: { xs: 1, sm: 0 } } : {}}
                    >
                      <Card 
                        variant={selectedStyle === style.id ? "outlined" : "plain"} 
                        sx={{ 
                          height: '100%',
                          width: { xs: '100%', sm: 'auto' },
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: 'sm',
                          },
                          display: 'flex',
                          flexDirection: 'column',
                          border: '1px solid',
                          borderColor: selectedStyle === style.id ? 'warning.500' : 'transparent',
                          overflow: 'hidden',
                          backgroundColor: selectedStyle === style.id ? 'warning.softBg' : 'white',
                          boxShadow: selectedStyle === style.id ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                          position: 'relative',
                          m: 1
                        }}
                        onClick={() => handleStyleSelect(style.id)}
                      >
                        {selectedStyle === style.id && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              bgcolor: 'warning.500',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              borderBottomLeftRadius: '4px',
                              zIndex: 2
                            }}
                          >
                            Selected
                          </Box>
                        )}
                        <Box sx={{ p: 2, flex: 1 }}>
                          <ImageCanvasViewer imageUrl={style.previewPath} overallWidthMm={width ?? null} overallHeightMm={height ?? null} heightPx={160} />
                        </Box>
                        <CardContent sx={{ 
                          p: 1.5,
                          borderTop: '1px solid',
                          borderColor: selectedStyle === style.id ? 'warning.400' : 'neutral.100',
                          textAlign: 'center',
                          backgroundColor: selectedStyle === style.id ? 'warning.100' : 'inherit'
                        }}>
                          <Typography 
                            level="title-sm" 
                            sx={{ 
                              fontWeight: selectedStyle === style.id ? 'bold' : 'normal',
                              color: selectedStyle === style.id ? 'warning.800' : 'neutral.800',
                            }}
                          >
                            {style.name}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                {/* Empty state when no styles match the filter */}
                {getWindowStyles().length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography level="body-lg">
                      No window styles found for this filter. Please try another category.
                    </Typography>
                  </Box>
                )}
                
                {/* Custom Pagination */}
                {getTotalPages() > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 2 }}>
                    <Stack 
                      direction={{ xs: 'column', sm: 'row' }} 
                      spacing={2} 
                      alignItems="center"
                      sx={{ width: '100%', maxWidth: '400px' }}
                    >
                      <Button
                        variant="outlined"
                        color="neutral"
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        startDecorator={<IconWrapper icon={FaChevronLeft} />}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Previous
                      </Button>
                      
                      <Typography level="body-md" sx={{ mx: 2 }}>
                        Page {currentPage} of {getTotalPages()}
                      </Typography>
                      
                      <Button
                        variant="outlined"
                        color="neutral"
                        onClick={goToNextPage}
                        disabled={currentPage === getTotalPages()}
                        endDecorator={<IconWrapper icon={FaChevronRight} />}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            // Step 3: Customize window
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography level="h2">
                  {editingItem ? 'Edit' : 'Customize Your'} {getTypeName(selectedType)}
                </Typography>
                
                <Button 
                  variant="outlined" 
                  color="neutral" 
                  onClick={() => setSelectedStyle(null)}
                >
                  Change Style
                </Button>
              </Box>
              
              {/* Display selected window style image at the top */}
              <Box 
                sx={{ 
                  width: '100%', 
                  display: 'flex', 
                  mb: 4,
                  p: 0
                }}
              >
                {/* Left side with window preview when editing */}
                {editingItem && (
                  <Box 
                    sx={{ 
                      flex: '0 0 250px',
                      mr: 3,
                      p: 2, 
                      bgcolor: 'warning.softBg',
                      borderRadius: 'md',
                      border: '2px solid',
                      borderColor: 'warning.outlinedBorder',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <Typography level="title-md" sx={{ mb: 2, color: 'warning.700' }}>
                      Currently Editing
                    </Typography>
                    
                    <Box 
                      sx={{ 
                        width: '100%', 
                        maxWidth: '200px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 2
                      }}
                    >
                      {originalStyle && (() => {
                        // Debug to ensure we're looking up the correct style
                        console.log("Rendering Currently Editing with originalStyle ID:", originalStyle);
                        
                        // Look up the style from allStyles (which includes ALL window styles)
                        // This is important because the style might not be in the filtered styles
                        const foundStyle = allStyles.find(style => style.id === originalStyle);
                        console.log("Found window style for display:", foundStyle);
                        
                        // Use the previewPath from the found style, or fall back to a default
                        const imagePath = foundStyle?.previewPath || '';
                        
                        return (
                          <ImageCanvasViewer imageUrl={imagePath} overallWidthMm={width ?? null} overallHeightMm={height ?? null} heightPx={200} />
                        );
                      })()}
                    </Box>
                    
                    <Typography level="body-sm" fontWeight="bold">
                      {originalStyle ? (() => {
                        // Look up the window style name from allStyles for display
                        const styleName = allStyles.find(style => style.id === originalStyle)?.name;
                        console.log("Style name for display:", styleName);
                        
                        // Return the style name if found, otherwise fallback to the ID
                        return styleName || `Style ${originalStyle}`;
                      })() : 'No style selected'}
                    </Typography>
                    
                    {editingItem?.reference && (
                      <Typography level="body-sm" color="primary" sx={{ mt: 1 }}>
                        Ref: {editingItem.reference}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {/* Right side or full width if not editing */}
                <Box 
                  sx={{ 
                    flex: 1,
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center',
                    bgcolor: '#f9f9f9',
                    p: 3,
                    pt: editingItem ? 5 : 3, // Add more padding at top when showing the title
                    borderRadius: 'md',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative', // Needed for absolute positioning of title
                  }}
                >
                  {/* Add title for right side to indicate this is the new selection */}
                  {editingItem && (
                    <Typography 
                      level="title-md" 
                      sx={{ 
                        position: 'absolute', 
                        top: '0.5rem', 
                        left: '50%', 
                        transform: 'translateX(-50%)',
                        backgroundColor: 'primary.softBg',
                        color: 'primary.700',
                        px: 2,
                        py: 0.5,
                        borderRadius: 'md',
                        fontWeight: 'bold',
                        zIndex: 2,
                      }}
                    >
                      New Selection
                    </Typography>
                  )}
                  
                  <Box 
                    sx={{ 
                      maxWidth: '250px', 
                      maxHeight: '220px',
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    {selectedStyle && (
                      <ImageCanvasViewer
                        imageUrl={getWindowStyles().find(style => style.id === selectedStyle)?.previewPath || ''}
                        overallWidthMm={width ?? null}
                        overallHeightMm={height ?? null}
                        heightPx={220}
                      />
                    )}
                    <Box sx={{ 
                      textAlign: 'center', 
                      mt: 2,
                      p: 1,
                      bgcolor: 'background.surface',
                      borderRadius: 'sm'
                    }}>
                      <Typography level="title-md">
                        {getWindowStyles().find(style => style.id === selectedStyle)?.name || `Style ${selectedStyle}`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              
              <Grid container spacing={4}>
                {/* Window Reference - Moved to beginning and made mandatory */}
                <Grid xs={12}>
                  <Card variant="outlined" sx={{ p: 2, border: '2px solid', borderColor: 'primary.300' }}>
                    <Typography level="title-lg" sx={{ mb: 2 }}>Window Reference *</Typography>
                    <FormControl sx={{ width: '100%' }} required>
                      <FormLabel>Reference for this specific window</FormLabel>
                      <Input
                        value={reference}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          console.log("Setting reference to:", e.target.value);
                          setReference(e.target.value);
                        }}
                        placeholder="Enter a reference for this window (e.g., 'Front Bedroom', 'Kitchen')"
                        required
                        sx={{
                          border: reference ? '1px solid #4caf50' : 'none',
                          '&:focus-within': {
                            border: '1px solid #4caf50'
                          }
                        }}
                      />
                    </FormControl>
                  </Card>
                </Grid>
                
                {/* Profile Type - For all window types */}
                <Grid xs={12}>
                  <Card variant="outlined" sx={{ p: 2, border: '2px solid', borderColor: 'primary.300' }}>
                    <Typography level="title-lg" sx={{ mb: 2 }}>Profile Type *</Typography>
                    <FormControl sx={{ width: '100%' }} required>
                      <FormLabel>Select the profile type for your window</FormLabel>
                      <select
                        value={profileType}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          console.log("Setting profile type to:", e.target.value);
                          setProfileType(e.target.value as 'Sculptured Profile' | 'Chamfered Profile');
                        }}
                        style={{
                          width: '100%',
                          padding: '10px',
                          fontSize: '1rem',
                          borderRadius: '8px',
                          border: profileType ? '1px solid #4caf50' : '1px solid #ccc',
                          marginTop: '8px'
                        }}
                        required
                      >
                        <option value="" disabled>Select a profile type</option>
                        <option value="Sculptured Profile">Sculptured Profile</option>
                        <option value="Chamfered Profile">Chamfered Profile</option>
                      </select>
                      {!profileType && (
                        <FormHelperText sx={{ color: 'danger.500' }}>
                          Profile type is required
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Card>
                </Grid>
                
                <Grid xs={12}>
                  <Card variant="outlined" sx={{ p: 2, border: '2px solid', borderColor: 'primary.300' }}>
                    <Typography level="title-lg" sx={{ mb: 2 }}>Beading Type *</Typography>
                    <FormControl sx={{ width: '100%' }} required>
                      <FormLabel>Select the beading type for your window</FormLabel>
                      <select
                        value={beadingType}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          setBeadingType(e.target.value as 'Sculptured (Ovolo)' | 'Chamfered');
                        }}
                        style={{
                          width: '100%',
                          padding: '10px',
                          fontSize: '1rem',
                          borderRadius: '8px',
                          border: '1px solid #4caf50',
                          marginTop: '8px'
                        }}
                        required
                      >
                        <option value="Sculptured (Ovolo)">Sculptured (Ovolo)</option>
                        <option value="Chamfered">Chamfered</option>
                      </select>
                    </FormControl>
                  </Card>
                </Grid>
                
                <Grid xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography level="title-lg" sx={{ mb: 2 }}>Window Options</Typography>
                    <FormControl sx={{ width: '100%', mb: 2 }}>
                      <FormLabel>Frame Color</FormLabel>
                      <RadioGroup
                        value={frameColor}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFrameColor(e.target.value)}
                        sx={{
                          gap: 1.5,
                          mt: 1,
                          maxHeight: '250px',
                          overflow: 'auto'
                        }}
                      >
                        <Radio value="white-smooth-both" label="White Smooth (both sides)" />
                        <Radio value="white-grain-both" label="White Grain (both sides)" />
                        <Radio value="agate-grey-both" label="Agate Grey (both sides)" />
                        <Radio value="black-noir-both" label="Black Noir (both sides)" />
                        <Radio value="anthracite-smooth-both" label="Anthracite Smooth (both sides)" />
                        <Radio value="golden-oak-both" label="Golden Oak (both sides)" />
                        <Radio value="honey-oak-both" label="Honey Oak (both sides)" />
                        <Radio value="irish-oak-both" label="Irish Oak (both sides)" />
                        <Radio value="rosewood-both" label="Rosewood (both sides)" />
                        <Radio value="spectral-grey-white-smooth" label="Spectral Grey on White Smooth" />
                      </RadioGroup>
                    </FormControl>
                    
                    <FormControl sx={{ width: '100%', mt: 2 }}>
                      <FormLabel>Profile Depth (mm)</FormLabel>
                      <Input
                        type="number"
                        value={profileDepth || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProfileDepth(e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="Default: 70mm"
                        slotProps={{
                          input: {
                            min: 0,
                            step: 1
                          }
                        }}
                      />
                    </FormControl>
                    
                    <FormControl sx={{ width: '100%', mt: 2 }}>
                      <Checkbox
                        label="Trickle Vents?"
                        checked={trickleVents}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrickleVents(e.target.checked)}
                      />
                      <FormHelperText>
                        Select if trickle vents are required for this window
                      </FormHelperText>
                    </FormControl>
                  </Card>
                </Grid>
                
                <Grid xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography level="title-lg" sx={{ mb: 2 }}>Glass Options</Typography>
                    <FormControl sx={{ width: '100%', mb: (glassType === 'clear' || glassType === 'frosted') ? 2 : 0 }}>
                      <FormLabel>Glass Type</FormLabel>
                      <RadioGroup
                        value={glassType}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGlassType(e.target.value)}
                        sx={{
                          gap: 1.5,
                          mt: 1
                        }}
                      >
                        <Radio value="clear" label="Clear Glass" />
                        <Radio value="frosted" label="Obscured Glass" />
                      </RadioGroup>
                    </FormControl>
                    
                    {/* Show clear glass options when clear glass is selected */}
                    {glassType === 'clear' && (
                      <FormControl sx={{ width: '100%', mt: 2 }}>
                        <FormLabel>Clear Glass Type</FormLabel>
                        <RadioGroup
                          value={clearGlassType}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClearGlassType(e.target.value as 'float' | 'toughened' | 'laminated')}
                          sx={{
                            gap: 1.5,
                            mt: 1
                          }}
                        >
                          <Radio value="float" label="Float" />
                          <Radio value="toughened" label="Toughened" />
                          <Radio value="laminated" label="Laminated" />
                        </RadioGroup>
                      </FormControl>
                    )}
                    
                    {/* Show obscured glass options when obscured glass is selected */}
                    {glassType === 'frosted' && (
                      <FormControl sx={{ width: '100%', mt: 2 }}>
                        <FormLabel>Obscured Glass Pattern</FormLabel>
                        <RadioGroup
                          value={obscuredGlassType}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setObscuredGlassType(e.target.value)}
                          sx={{
                            gap: 1.5,
                            mt: 1,
                            maxHeight: '250px',
                            overflow: 'auto'
                          }}
                        >
                          <Radio value="Arctic" label="Arctic" />
                          <Radio value="Autumn" label="Autumn" />
                          <Radio value="Chantilly" label="Chantilly" />
                          <Radio value="Charcoal Sticks" label="Charcoal Sticks" />
                          <Radio value="Contora" label="Contora" />
                          <Radio value="Cotswold" label="Cotswold" />
                          <Radio value="Digital" label="Digital" />
                          <Radio value="Everglade" label="Everglade" />
                          <Radio value="Flemish" label="Flemish" />
                          <Radio value="Floriellle" label="Floriellle" />
                          <Radio value="Mayflower" label="Mayflower" />
                          <Radio value="Minster" label="Minster" />
                          <Radio value="Oak" label="Oak" />
                          <Radio value="Pelerine" label="Pelerine" />
                          <Radio value="Stippolyte" label="Stippolyte" />
                          <Radio value="Sycamore" label="Sycamore" />
                          <Radio value="Taffeta" label="Taffeta" />
                          <Radio value="Warwick" label="Warwick" />
                        </RadioGroup>
                      </FormControl>
                    )}
                  </Card>
                </Grid>
                
                <Grid xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography level="title-lg" sx={{ mb: 2 }}>Dimensions</Typography>
                    <FormControl sx={{ width: '100%', mb: 2 }}>
                      <FormLabel>Width (mm)</FormLabel>
                      <Input
                        type="number"
                        value={width || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWidth(e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="Enter width in mm"
                        slotProps={{
                          input: {
                            min: 0,
                            step: 1
                          }
                        }}
                      />
                    </FormControl>
                    
                    <FormControl sx={{ width: '100%', mb: 2 }}>
                      <FormLabel>Height (mm)</FormLabel>
                      <Input
                        type="number"
                        value={height || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHeight(e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="Enter height in mm"
                        slotProps={{
                          input: {
                            min: 0,
                            step: 1
                          }
                        }}
                      />
                    </FormControl>
                    
                    <FormControl sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox 
                          checked={cillIncluded} 
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCillIncluded(e.target.checked)}
                          label="Cill included?"
                        />
                        <Typography level="body-sm" sx={{ ml: 1, color: 'neutral.600' }}>
                          (default cill height is assumed 30mm)
                        </Typography>
                      </Box>
                    </FormControl>
                  </Card>
                </Grid>
                
                <Grid xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography level="title-lg" sx={{ mb: 2 }}>Cill Options</Typography>
                    
                    {!cillIncluded && (
                      <FormControl sx={{ width: '100%', mb: 2 }}>
                        <FormLabel>Cill Required?</FormLabel>
                        <RadioGroup
                          value={cillRequired ? 'yes' : 'no'}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCillRequired(e.target.value === 'yes')}
                          orientation="horizontal"
                          sx={{ gap: 2, mt: 1 }}
                        >
                          <Radio value="yes" label="Yes" />
                          <Radio value="no" label="No" />
                        </RadioGroup>
                      </FormControl>
                    )}
                    
                    {cillIncluded && (
                      <Box sx={{ mb: 2 }}>
                        <Typography level="body-md">
                          Cill is included in the overall window height.
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Show Cill Type when cillIncluded is true or when cillRequired is true */}
                    {(cillIncluded || (!cillIncluded && cillRequired)) && (
                      <FormControl sx={{ width: '100%', mb: 2 }}>
                        <FormLabel>Cill Type</FormLabel>
                        <RadioGroup
                          value={cillType}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCillType(e.target.value as 'Regular' | 'Stub' | '')}
                          sx={{ gap: 1.5, mt: 1 }}
                        >
                          <Radio value="Regular" label="Regular Cill" />
                          <Radio value="Stub" label="Stub Cill" />
                        </RadioGroup>
                      </FormControl>
                    )}
                    
                    {/* Only show Cill Height when cillRequired is true and cillIncluded is false */}
                    {(!cillIncluded && cillRequired) && (
                      <FormControl sx={{ width: '100%' }}>
                        <FormLabel>Cill Height (mm)</FormLabel>
                        <Input
                          type="number"
                          value={cillHeight}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCillHeight(e.target.value ? Number(e.target.value) : 30)}
                          placeholder="Enter cill height in mm"
                          slotProps={{
                            input: {
                              min: 0,
                              step: 1
                            }
                          }}
                        />
                      </FormControl>
                    )}
                  </Card>
                </Grid>
                
                <Grid xs={12}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography level="title-lg" sx={{ mb: 2 }}>Additional Notes</Typography>
                    <FormControl sx={{ width: '100%' }}>
                      <Textarea
                        minRows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Enter any additional requirements or notes"
                      />
                    </FormControl>
                  </Card>
                </Grid>
              </Grid>
              
              <Box 
                sx={{ 
                  mt: 4, 
                  p: 3, 
                  bgcolor: 'background.level1', 
                  borderRadius: 'md',
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center'
                }}
              >
                <Typography level="h4" mb={3}>
                  {getTypeName(selectedType)} - {getWindowStyles().find(style => style.id === selectedStyle)?.name || ''}
                </Typography>
                
                {/* Add quantity control */}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
                  <Typography level="body-lg" sx={{ mr: 2 }}>Quantity:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      variant="outlined"
                      color="neutral"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <IconWrapper icon={FaMinus} />
                    </IconButton>
                    
                    <Input
                      type="number"
                      value={quantity}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value > 0) {
                          setQuantity(value);
                        } else if (e.target.value === '') {
                          setQuantity(1);
                        }
                      }}
                      slotProps={{
                        input: {
                          min: 1,
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{ width: '80px' }}
                    />
                    
                    <IconButton
                      variant="outlined"
                      color="neutral"
                      onClick={() => setQuantity(quantity + 1)}
                    >
                      <IconWrapper icon={FaPlus} />
                    </IconButton>
                  </Box>
                </Box>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                  {editingItem ? (
                    <>
                      <Button
                        size="lg"
                        color="neutral"
                        variant="outlined"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="lg"
                        color="primary"
                        variant="outlined"
                        onClick={() => {
                          // When clicking "Change Style", we want to:
                          // 1. Keep the editingItem state intact
                          // 2. Temporarily clear the selectedStyle to show the style selection UI
                          // 3. Keep all other form values the same
                          setSelectedStyle(null);
                        }}
                      >
                        Change Style
                      </Button>
                      <Button
                        size="lg"
                        color="primary"
                        variant="solid"
                        onClick={handleSaveEdit}
                        id="save-window-item-button"
                        disabled={!reference.trim() || !profileType}
                      >
                        Save Changes
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="lg"
                        color="neutral"
                        variant="outlined"
                        onClick={() => setSelectedStyle(null)}
                      >
                        Choose Different Style
                      </Button>
                      <Button
                        size="lg"
                        color="primary"
                        variant="solid"
                        onClick={handleAddToQuote}
                        disabled={!reference.trim() || !profileType}
                      >
                        Add to Quote
                      </Button>
                    </>
                  )}
                </Stack>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
      
      {/* Window Basket Drawer */}
      <Drawer
        size="lg"
        variant="plain"
        anchor="right"
        open={basketOpen}
        onClose={() => {
          // Close the basket
          setBasketOpen(false);
          
          // Check if we are editing but the basket is now empty
          if (editingItem && items.length === 0) {
            resetEditorState('Editor reset because the item was removed from the basket');
          }
        }}
        slotProps={{
          content: {
            sx: {
              bgcolor: 'background.surface',
              p: 2,
              boxShadow: 'lg',
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }
          }
        }}
      >
        <WindowBasket 
          onClose={() => setBasketOpen(false)}
          onEditItem={(item) => {
            setBasketOpen(false);
            handleEditItem(item);
          }}
        />
      </Drawer>
      
      {testNotification && (
        <Alert 
          variant="soft"
          color="primary" 
          sx={{ 
            mb: 2,
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 9999,
            minWidth: 300
          }}
          endDecorator={
            <IconButton 
              variant="plain" 
              size="sm" 
              onClick={() => setTestNotification(null)}
            >
              <IconWrapper icon={FaTimes} />
            </IconButton>
          }
        >
          {testNotification}
        </Alert>
      )}
    </Container>
  );
};

export default WindowDesigner; 